import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TrialExpiredModal.css';

type TrialExpiredModalVariant = 'expired' | 'extension' | 'book-demo-for-extension';

interface TrialExpiredModalProps {
  onClose: () => void;
  variant?: TrialExpiredModalVariant;
  onExtend?: () => Promise<void>;
  onBookDemo?: () => void;
}

const TrialExpiredModal = ({
  onClose,
  variant = 'expired',
  onExtend,
  onBookDemo,
}: TrialExpiredModalProps) => {
  const navigate = useNavigate();
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);

  const handleViewPlans = () => {
    navigate('/plans');
    onClose();
  };

  const handleExtendTrial = async () => {
    if (!onExtend) {
      return;
    }

    setExtendError(null);
    setIsExtending(true);

    try {
      await onExtend();
    } catch (error) {
      setExtendError(error instanceof Error ? error.message : 'Failed to extend trial. Please try again.');
    } finally {
      setIsExtending(false);
    }
  };

  const isExtensionVariant = variant === 'extension';
  const isBookDemoVariant = variant === 'book-demo-for-extension';

  const title = isExtensionVariant || isBookDemoVariant ? 'Your trial has ended' : 'Your trial has expired';

  const message = isBookDemoVariant
    ? 'Your 7-day trial period has ended. To extend your access for 7 additional days, please book a demo with our team first. Once your demo is booked, you will be able to extend your trial.'
    : isExtensionVariant
      ? 'Your 7-day trial period has ended. You have booked a demo, so you can now extend your trial once for 7 additional days to keep exploring DISGENET.'
      : 'Your trial period has ended. To continue accessing DISGENET features, please choose a plan that fits your needs.';

  return (
    <div className="trial-modal-overlay">
      <div className="trial-modal-content" onClick={e => e.stopPropagation()}>
        <div className="trial-modal-icon">
          <i className="bi bi-exclamation-triangle-fill"></i>
        </div>

        <h2 className="trial-modal-title">{title}</h2>

        <p className="trial-modal-message">{message}</p>

        {extendError && <p className="trial-modal-error">{extendError}</p>}

        <div className="trial-modal-actions">
          {isBookDemoVariant && (
            <button className="btn-gradient-blue trial-modal-btn-primary" onClick={onBookDemo}>
              Book a Demo
            </button>
          )}
          {isExtensionVariant && (
            <button
              className="btn-gradient-blue trial-modal-btn-primary"
              onClick={handleExtendTrial}
              disabled={isExtending}
            >
              {isExtending ? 'Extending trial...' : 'Extend trial 7 days'}
            </button>
          )}
          <button
            className={`${isExtensionVariant || isBookDemoVariant ? 'trial-modal-btn-secondary' : 'btn-gradient-pink trial-modal-btn-primary'}`}
            onClick={handleViewPlans}
            disabled={isExtending}
          >
            View Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
